import { useContext, useEffect } from "react";

import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";

import CircularProgress from "shared/components/atoms/CircularProgress";
import api from "shared/infra/services/api";
import { recoverUserInformation } from "shared/infra/services/auth/requests-api";
import GlobalContext from "shared/providers/context/GlobalContext";
import { User } from "shared/providers/context/types/auth-context";
import { setCookiesPromoterLink } from "shared/utils/affiliates/setCookiesPromoterLink";
import { setPromoterClick } from "shared/utils/affiliates/setPromoterClicks";
import { makeCustomNotFoundPath } from "shared/utils/customNotFound";
import { useFeature } from "shared/utils/hooks/useFeature";
import { saveNotFoundURLInCookies } from "shared/utils/saveNotFoundURLInCookies";

import { CheckoutTemplate } from "@payment/components/CheckoutTemplate";
import { getProductData } from "@payment/g/[uuid]/utils/requests";
import { useCheckoutPageInitialRequests } from "@payment/hooks/useCheckoutPageInitialRequests";
import Maintenance from "@payment/pages/Maintenance";
import CheckoutProvider from "@payment/providers/CheckoutProvider";
import { ICheckoutPage } from "@payment/providers/CheckoutProvider/dtos";
import { captureException, captureMessage } from "@sentry/nextjs";

const Checkout: NextPage<ICheckoutPage> = ({
  initialData,
  productData,
  promoterLink,
  clientIp,
}: ICheckoutPage) => {
  if (productData?.maintenance?.checkout) return <Maintenance />;

  const { isFallback, push } = useRouter();
  const { loadPixelResponse, pixelResponse } = useCheckoutPageInitialRequests();

  useEffect(
    function pixelInitialFetch() {
      try {
        loadPixelResponse(productData?.group?.slug, promoterLink);
      } catch (error) {
        console.log("Erro (Checkout | pixelInitialFetch): ", error);
        captureException(error);
        captureMessage("Erro (Checkout | pixelInitialFetch)");
      }
    },
    [productData, promoterLink],
  );

  const { state } = useContext(GlobalContext);
  const isMobileAppClient = state.isMobileAppClient;

  const isCheckoutLinkEnabled = useFeature("f_checkout_link");
  if (!isCheckoutLinkEnabled || isMobileAppClient) {
    push("/not-found");
    return <CircularProgress />;
  }

  if (isFallback) return <CircularProgress />;

  return (
    <CheckoutProvider
      addressListInitialState={[]}
      userSettingsInitialState={{}}
      creditCardsInitialState={[]}
      pixelResponseInitialState={pixelResponse}
      clientIp={clientIp}
    >
      <CheckoutTemplate
        initialData={initialData}
        promoterLink={promoterLink}
        productData={productData}
        clientIp={clientIp}
      />
    </CheckoutProvider>
  );
};

export default Checkout;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const uuid = context.params?.uuid;
  const { token: access_token, username: stringfiedUsername } =
    context.req.cookies;
  const token = access_token ? JSON?.parse(access_token) : null;
  const clientIp =
    context?.req?.headers?.["x-forwarded-for"] ||
    context?.req?.connection?.remoteAddress;

  const headers = {
    Authorization: "Bearer " + token,
    "X-Requested-With": "XMLHttpRequest",
  };

  const getUser = async (): Promise<User> => {
    try {
      if (token && stringfiedUsername) {
        const username = JSON.parse(stringfiedUsername || "");
        const response = await recoverUserInformation(username, api);

        return response?.data;
      }

      return null;
    } catch (error) {
      console.log(error);
    }
  };

  const productData = await getProductData(uuid as string, headers);

  const productWasFounded = !!productData;
  if (!productWasFounded) {
    const currentUrlThatIsNotFounded = context?.req?.url;
    saveNotFoundURLInCookies(currentUrlThatIsNotFounded, context);

    return {
      redirect: {
        destination: makeCustomNotFoundPath({
          itemSlug: uuid as string,
          page: "offer",
        }),
        permanent: false,
      },
    };
  }

  const offerIsAvaliable = !!productData.enabled_and_current;
  if (!offerIsAvaliable)
    return {
      redirect: {
        destination: `/payment/g/${uuid}/unavailable`,
        permanent: false,
      },
    };

  let promoterLink = "";

  const productHasActivePromoter = !!productData?.group?.promoter_active;
  if (productHasActivePromoter) {
    const ref = context?.query?.ref as string;

    if (!!ref) {
      const user = await getUser();

      promoterLink = setCookiesPromoterLink(
        context,
        ref,
        productData?.group?.slug,
        productData?.group?.promoter_commission,
      );

      setPromoterClick({
        group_id: productData?.group?.id,
        promoter_link: ref,
        user_id: user?.id,
        offer_id: productData?.offer?.id,
      });
    } else {
      promoterLink = context?.req?.cookies[productData?.group?.slug];
    }
  }

  const { name, phone, email, document } = context?.query;

  return {
    props: {
      productData,
      initialData: {
        name: name || null,
        phone: phone || null,
        email: email || null,
        document: document || null,
      },
      promoterLink: promoterLink ? promoterLink : null,
      clientIp,
    },
  };
};
